import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  RadioGroup,
  FormLabel,
  Divider,
  TextField,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { ListingPolicy, ListingType, ListingStatus } from '../../../data/types/domain';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ForeverIcon from '@mui/icons-material/AllInclusive';
import { FormikProps } from 'formik';
import * as Yup from 'yup';
import { format, differenceInDays, parseISO } from 'date-fns';
import { formatDateForInput, getToday, get30DaysFromToday } from '../../../utils/dateUtils';

interface ListingPolicyStepProps {
  formik?: FormikProps<any>;
  initialData?: Omit<ListingPolicy, "id" | "created_at" | "updated_at" | "business_id">;
}

const validationSchema = Yup.object().shape({
  values: Yup.object().shape({
    listing_type: Yup.string()
      .required('Listing type is required')
      .oneOf(Object.values(ListingType), 'Invalid listing type'),
    listing_status: Yup.string()
      .required('Listing status is required')
      .oneOf(Object.values(ListingStatus), 'Invalid listing status'),
    valid_from: Yup.date()
      .required('Valid from date is required'),
    valid_to: Yup.date()
      .nullable()
      .when('never_expires', {
        is: false,
        then: (schema) => schema.min(
          Yup.ref('valid_from'), 
          'Valid to date must be after valid from date'
        ).required('Valid to date is required'),
        otherwise: (schema) => schema.nullable()
      }),
    never_expires: Yup.boolean()
  })
});

export interface ListingPolicyStepRef {
  isValid: () => Promise<boolean>;
  getErrors: () => Promise<string[]>;
  getData: () => Promise<any>;
}

export const ListingPolicyStep = forwardRef<ListingPolicyStepRef, ListingPolicyStepProps>(({ formik, initialData }, ref) => {
  const defaultValidFrom = initialData?.valid_from ? formatDateForInput(initialData.valid_from) : getToday();
  const defaultValidTo = initialData?.valid_to ? formatDateForInput(initialData.valid_to) : get30DaysFromToday();
  
  const [values, setValues] = useState(initialData || {
    listing_type: ListingType.TRIAL,
    listing_status: ListingStatus.ACTIVE,
    valid_from: defaultValidFrom,
    valid_to: defaultValidTo,
    never_expires: false,
  });

  const [validationError, setValidationError] = useState<string | null>(null);
  const [daysValid, setDaysValid] = useState<number>(30);

  useEffect(() => {
    try {
      // Only calculate days if not set to never expire
      if (!values.never_expires && values.valid_from && values.valid_to) {
        // Calculate days between valid_from and valid_to
        const fromDate = typeof values.valid_from === 'string' 
          ? parseISO(values.valid_from) 
          : values.valid_from;
        
        const toDate = typeof values.valid_to === 'string' 
          ? parseISO(values.valid_to) 
          : values.valid_to;
        
        if (fromDate && toDate) {
          const days = differenceInDays(toDate, fromDate) + 1; // +1 to include the start day
          setDaysValid(days > 0 ? days : 0);
          
          // Validate that valid_to is after valid_from
          if (days <= 0) {
            setValidationError('Valid to date must be after valid from date');
          } else {
            setValidationError(null);
          }
        }
      } else {
        setValidationError(null);
      }
    } catch (err) {
      console.error('Error calculating date difference:', err);
    }
  }, [values.valid_from, values.valid_to, values.never_expires]);

  const { errors, touched } = formik || {};

  useImperativeHandle(ref, () => ({
    isValid: async () => {
      try {
        await validationSchema.validate({ values }, { abortEarly: false });
        return validationError === null;
      } catch (err) {
        return false;
      }
    },
    getErrors: async () => {
      try {
        await validationSchema.validate({ values }, { abortEarly: false });
        if (validationError) {
          return [validationError];
        }
        return [];
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          return err.errors;
        }
        return ['An unknown error occurred'];
      }
    },
    getData: async () => {
      // If never_expires is true, we set valid_to to null
      if (values.never_expires) {
        return { ...values, valid_to: null };
      }
      return values;
    }
  }));

  const handleListingTypeChange = (type: ListingType) => {
    setValues(prev => ({ ...prev, listing_type: type }));
  };

  const handleListingStatusChange = (status: ListingStatus) => {
    setValues(prev => ({ ...prev, listing_status: status }));
  };

  const handleDateChange = (field: 'valid_from' | 'valid_to') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValues(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleNeverExpiresToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues(prev => ({ ...prev, never_expires: e.target.checked }));
  };

  const getListingTypeLabel = (type: ListingType) => {
    switch (type) {
      case ListingType.FREE:
        return 'Free';
      case ListingType.PAID:
        return 'Paid';
      case ListingType.TRIAL:
        return 'Trial';
      default:
        return 'Unknown';
    }
  };

  const getListingTypeDescription = (type: ListingType) => {
    switch (type) {
      case ListingType.FREE:
        return 'Basic listing with limited features';
      case ListingType.PAID:
        return 'Full featured listing with premium benefits';
      case ListingType.TRIAL:
        return 'Temporary access to premium features';
      default:
        return '';
    }
  };

  const getListingTypeIcon = (type: ListingType) => {
    switch (type) {
      case ListingType.FREE:
        return <StarBorderIcon />;
      case ListingType.PAID:
        return <StarIcon />;
      case ListingType.TRIAL:
        return <CheckCircleIcon />;
      default:
        return null;
    }
  };

  const getListingStatusSummary = () => {
    const status = values.listing_status === ListingStatus.ACTIVE ? 'active' : 'inactive';
    const fromDate = typeof values.valid_from === 'string' 
      ? format(parseISO(values.valid_from), 'MMM dd, yyyy')
      : format(values.valid_from, 'MMM dd, yyyy');
    
    if (values.never_expires) {
      return `This ${getListingTypeLabel(values.listing_type).toLowerCase()} listing will be ${status} from ${fromDate} with no expiration date.`;
    } else {
      const toDate = values.valid_to 
        ? (typeof values.valid_to === 'string'
            ? format(parseISO(values.valid_to as string), 'MMM dd, yyyy')
            : format(values.valid_to as Date, 'MMM dd, yyyy'))
        : '';
      
      return `This ${getListingTypeLabel(values.listing_type).toLowerCase()} listing will be ${status} for ${daysValid} days (${fromDate} to ${toDate}).`;
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Choose how you want the business to be listed on our platform.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormLabel component="legend" sx={{ mb: 2, display: 'block' }}>
            Listing Type
          </FormLabel>
          
          <RadioGroup
            value={values.listing_type || ListingType.FREE}
            onChange={(e) => setValues(prev => ({ ...prev, listing_type: e.target.value as ListingType }))}
            sx={{ mb: 2 }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}>
              {Object.values(ListingType).map((type) => (
                <Paper
                  key={type}
                  elevation={values.listing_type === type ? 3 : 1}
                  sx={{
                    p: 2,
                    flex: { xs: '1 1 auto', sm: 1 },
                    display: 'flex',
                    flexDirection: { xs: 'row', sm: 'column' },
                    alignItems: { xs: 'center', sm: 'center' },
                    justifyContent: { xs: 'flex-start', sm: 'center' },
                    cursor: 'pointer',
                    border: values.listing_type === type ? '2px solid' : '1px solid',
                    borderColor: values.listing_type === type ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    },
                  }}
                  onClick={() => setValues(prev => ({ ...prev, listing_type: type }))}
                >
                  <Box sx={{ 
                    color: 'primary.main', 
                    mb: { xs: 0, sm: 1 },
                    mr: { xs: 2, sm: 0 }
                  }}>
                    {getListingTypeIcon(type)}
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: { xs: 'flex-start', sm: 'center' }
                  }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {getListingTypeLabel(type)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      align="left"
                      sx={{ 
                        mt: { xs: 0.5, sm: 1 },
                        textAlign: { xs: 'left', sm: 'center' }
                      }}
                    >
                      {getListingTypeDescription(type)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          </RadioGroup>
          
          {touched?.listingPolicy && (touched.listingPolicy as Record<string, boolean>)?.listing_type && errors?.listingPolicy && (errors.listingPolicy as Record<string, string>)?.listing_type && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {(errors.listingPolicy as Record<string, string>).listing_type}
            </Typography>
          )}
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
        </Grid>
        
        <Grid item xs={12}>
          <FormLabel component="legend" sx={{ mb: 2, display: 'block' }}>
            Listing Status
          </FormLabel>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 2 
          }}>
            <Paper
              elevation={values.listing_status === ListingStatus.ACTIVE ? 3 : 1}
              sx={{
                p: 2,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                border: values.listing_status === ListingStatus.ACTIVE ? '2px solid' : '1px solid',
                borderColor: values.listing_status === ListingStatus.ACTIVE ? 'success.main' : 'divider',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'success.main',
                  boxShadow: 2,
                },
              }}
              onClick={() => setValues(prev => ({ ...prev, listing_status: ListingStatus.ACTIVE }))}
            >
              <CheckCircleIcon color="success" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Active
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your listing will be visible to users
                </Typography>
              </Box>
            </Paper>
            
            <Paper
              elevation={values.listing_status === ListingStatus.INACTIVE ? 3 : 1}
              sx={{
                p: 2,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                border: values.listing_status === ListingStatus.INACTIVE ? '2px solid' : '1px solid',
                borderColor: values.listing_status === ListingStatus.INACTIVE ? 'error.main' : 'divider',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'error.main',
                  boxShadow: 2,
                },
              }}
              onClick={() => setValues(prev => ({ ...prev, listing_status: ListingStatus.INACTIVE }))}
            >
              <CancelIcon color="error" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Inactive
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your listing will be hidden from users
                </Typography>
              </Box>
            </Paper>
          </Box>
          
          {touched?.listingPolicy && (touched.listingPolicy as Record<string, boolean>)?.listing_status && errors?.listingPolicy && (errors.listingPolicy as Record<string, string>)?.listing_status && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {(errors.listingPolicy as Record<string, string>).listing_status}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
        </Grid>

        <Grid item xs={12}>
          <FormLabel component="legend" sx={{ mb: 2, display: 'block' }}>
            Listing Validity Period
          </FormLabel>
          
          <FormControlLabel
            control={
              <Switch
                checked={values.never_expires}
                onChange={handleNeverExpiresToggle}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ForeverIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2">Never Expires</Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body2" fontWeight="medium">
                  Valid From
                </Typography>
              </Box>
              <TextField
                fullWidth
                type="date"
                value={formatDateForInput(values.valid_from)}
                onChange={handleDateChange('valid_from')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EventAvailableIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body2" fontWeight="medium">
                  Valid To
                </Typography>
              </Box>
              <TextField
                fullWidth
                type="date"
                value={values.valid_to ? formatDateForInput(values.valid_to) : ''}
                onChange={handleDateChange('valid_to')}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!validationError}
                helperText={validationError}
                disabled={values.never_expires}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Alert 
            severity={validationError ? 'error' : 'info'} 
            icon={validationError ? undefined : values.never_expires ? <ForeverIcon /> : <EventAvailableIcon />}
            sx={{ 
              mt: 2, 
              borderRadius: 2,
              backgroundColor: theme => validationError 
                ? theme.palette.error.light 
                : theme.palette.info.light,
              color: theme => validationError 
                ? theme.palette.error.contrastText 
                : theme.palette.info.contrastText,
            }}
          >
            {validationError ? validationError : getListingStatusSummary()}
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );
}); 